import React, { useEffect, useState } from 'react';

/**
 * Auto-scrolling banner carousel (right → left).
 * - Each banner stays visible 3s before fading to the next.
 * - Fully responsive: max-width 100%, height auto via object-fit.
 * - Won't overflow the parent container.
 * - Anti-crop CSS: configurable aspect ratio + objectFit.
 *
 * PERFORMANCE OPTIMIZATIONS (2026-02):
 * - <img loading="lazy"> for non-first slides.
 * - <img decoding="async"> for non-blocking image decode.
 * - fetchPriority hint: "high" for the first visible image, "low" for others.
 * - Explicit width/height to prevent CLS.
 * - Preload the first banner via <link rel="preload"> injected once.
 * - GPU-accelerated transitions (transform + opacity only, no layout).
 * - will-change hint tells the compositor to prepare a layer up front → no jank.
 */
const DEFAULT_BANNERS = [
  {
    url: 'https://customer-assets.emergentagent.com/job_palette-preview-12/artifacts/z4z3bhto_file_000000009c3c71f59c61b0fa2b774a61.png',
    alt: 'Airdrop Challenge — Human vs AI',
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_palette-preview-12/artifacts/zryc3qze_file_00000000044c722fa85ebe8b977982bf.png',
    alt: 'New Games — More ways to play',
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_palette-preview-12/artifacts/o3aejzc7_file_0000000095a071f5bfca3f1f32b27d0f.png',
    alt: 'Presale Premier Phase is Live',
  },
];

// Preload the first banner once per page (module-level guard, no double insertions)
let __bannerPreloaded = false;
const preloadFirstBanner = (url) => {
  if (typeof document === 'undefined' || __bannerPreloaded) return;
  __bannerPreloaded = true;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  link.fetchPriority = 'high';
  document.head.appendChild(link);
};

const BannerCarousel = ({ banners = DEFAULT_BANNERS, intervalMs = 3000, aspectRatio = '1200 / 350', objectFit = 'cover' }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (banners.length > 0) preloadFirstBanner(banners[0].url);
  }, [banners]);

  useEffect(() => {
    if (!banners.length) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % banners.length), intervalMs);
    return () => clearInterval(id);
  }, [banners.length, intervalMs]);

  if (!banners.length) return null;

  return (
    <div
      className="w-full rounded-2xl overflow-hidden shadow-lg relative"
      style={{ aspectRatio, maxWidth: '100%', background: '#0a0f1c', contain: 'layout paint size' }}
      data-testid="banner-carousel"
    >
      {banners.map((b, i) => (
        <img
          key={b.url}
          src={b.url}
          alt={b.alt}
          width={1200}
          height={675}
          loading={i === 0 ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={i === 0 ? 'high' : 'low'}
          className="absolute inset-0 w-full h-full"
          style={{
            objectFit,
            objectPosition: 'center',
            opacity: i === index ? 1 : 0,
            transform: i === index ? 'translate3d(0,0,0)' : 'translate3d(4%,0,0)',
            transition: 'opacity 700ms ease-in-out, transform 700ms ease-in-out',
            willChange: 'opacity, transform',
            backfaceVisibility: 'hidden',
          }}
          data-testid={`banner-slide-${i}`}
        />
      ))}
      {/* Dot indicators */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {banners.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: i === index ? 18 : 6,
              background: i === index ? '#fbbf24' : 'rgba(255,255,255,0.55)',
            }}
            data-testid={`banner-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
};

export default BannerCarousel;
